import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { ATTIRE_PRESET_OPTIONS, EVENT_FORMAT_OPTIONS, LANGUAGE_OPTIONS } from './constants';
import type { FinishStepProps } from './types';
import {
  formatDate,
  formatTime,
  getEntrySummary,
  getPostingSummary,
  getPickerValue,
} from './utils';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { cn } from '@/lib/utils';
import { EntryTypeTicketed } from '@/lib/api/apiTypes';
import { formatEventAgeRestriction, getEventFormatLabel, getEventTagLabel } from '@/features/events/eventTags';
import { getNextDressCodeFromPreset, isDressCodePresetSelected } from '@/features/events/eventMobileHelpers';
import DateTimePicker, { type DateTimePickerEvent } from '@react-native-community/datetimepicker';
import { CheckIcon, ChevronDownIcon } from 'lucide-react-native';
import * as React from 'react';
import { Platform, Pressable, Switch, View } from 'react-native';

function TagChip({
  tagValue,
  label,
  active,
  disabled,
  onPress,
}: {
  tagValue: string;
  label: string;
  active: boolean;
  disabled: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      className="flex-row items-center gap-2 rounded-full border px-4 py-3"
      style={{
        opacity: disabled ? 0.42 : 1,
        borderColor: active ? DESIGN_TOKENS.colors.accentStrong : DESIGN_TOKENS.colors.borderStrong,
        backgroundColor: active ? DESIGN_TOKENS.colors.accentStrong : DESIGN_TOKENS.colors.surfaceAlt,
      }}
      disabled={disabled}
      onPress={onPress}>
      <View className="size-2.5 rounded-full" style={{ backgroundColor: DESIGN_TOKENS.colors.textSoft }} />
      <Text
        variant="small"
        className="font-semibold"
        style={{ color: active ? DESIGN_TOKENS.colors.textInverse : DESIGN_TOKENS.colors.text }}>
        {label}
      </Text>
    </Pressable>
  );
}

function FieldMetaLabel({
  label,
  meta,
}: {
  label: string;
  meta?: 'Optional' | 'Required';
}) {
  return (
    <View className="flex-row items-center gap-2 px-1">
      <Text variant="small" className="font-semibold text-muted-foreground">
        {label}
      </Text>
      {meta ? (
        <Text variant="small" className="font-medium" style={{ color: DESIGN_TOKENS.colors.textSoft }}>
          {meta}
        </Text>
      ) : null}
    </View>
  );
}

function PickerTrigger({ label, placeholder, onPress }: { label: string; placeholder?: string; onPress: () => void }) {
  const isEmpty = !label.trim();
  return (
    <Pressable
      onPress={onPress}
      className="flex-1 rounded-[16px] border px-4 py-3.5"
      style={{
        borderColor: DESIGN_TOKENS.colors.borderStrong,
        backgroundColor: DESIGN_TOKENS.colors.surfaceRaised,
      }}>
      <Text
        variant="default"
        style={{ color: isEmpty ? DESIGN_TOKENS.colors.textMuted : DESIGN_TOKENS.colors.text }}>
        {isEmpty ? (placeholder ?? '—') : label}
      </Text>
    </Pressable>
  );
}

function getPosterSummary(draft: FinishStepProps['draft']) {
  const uploadedCount = draft.posters.filter((poster) => poster.status === 'uploaded').length;
  const failedCount = draft.posters.filter((poster) => poster.status === 'failed').length;
  const uploadingCount = draft.posters.filter((poster) => poster.status === 'uploading').length;

  if (uploadedCount > 0) {
    return `${uploadedCount} poster${uploadedCount === 1 ? '' : 's'} ready`;
  }
  if (uploadingCount > 0) {
    return 'Still uploading';
  }
  if (failedCount > 0) {
    return 'Upload needs attention';
  }
  return 'Missing';
}

export function FinishStep({
  mode,
  draft,
  linkedVenueID,
  startsAt,
  flyerReview,
  attributes,
  isLoadingAttributes,
  attributeLoadError,
  activePicker,
  onChange,
  onOpenPicker,
  onClosePicker,
  onPickerChange,
}: FinishStepProps) {
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const [focusedField, setFocusedField] = React.useState<'shortDescription' | null>(null);

  const advancedFilledCount = [
    draft.dressCode.trim(),
    draft.lastEntryTime.trim(),
    draft.parkingInfo.trim(),
    draft.presentedBy.trim(),
    draft.weatherDependent,
    draft.contactEmail.trim(),
    draft.contactPhone.trim(),
    draft.eventUrl.trim(),
    draft.internalNotes.trim(),
  ].filter(Boolean).length;

  const hairline = { height: 1, backgroundColor: DESIGN_TOKENS.colors.borderStrong, marginHorizontal: 16 } as const;

  return (
    <View className="gap-7">
      <View className="gap-2">
        <View className="flex-row items-baseline justify-between px-1">
          <Text variant="small" className="font-semibold text-muted-foreground">One-line hook · Optional</Text>
          <Text
            variant="small"
            style={{ color: draft.shortDescription.length > 120 ? '#ef4e2e' : DESIGN_TOKENS.colors.textMuted }}>
            {draft.shortDescription.length}/140
          </Text>
        </View>
        <Textarea
          value={draft.shortDescription}
          onChangeText={(value) => onChange({ shortDescription: value.slice(0, 140) })}
          onFocus={() => {
            onClosePicker();
            setFocusedField('shortDescription');
          }}
          onBlur={() => setFocusedField((current) => (current === 'shortDescription' ? null : current))}
          placeholder="e.g. House all night - open bar until midnight"
          scrollEnabled={false}
          className={cn(
            'min-h-[96px] rounded-[20px] border-border-strong bg-surface-alt p-4 text-[15px] leading-[22px] dark:bg-surface-alt',
            focusedField === 'shortDescription' && 'border-primary'
          )}
        />
      </View>

      <View className="gap-2">
        <FieldMetaLabel label="Event format" meta="Optional" />
        <Text variant="small" className="px-1 text-muted-foreground">
          Leave this blank if none of these really fit.
        </Text>
        <View className="overflow-hidden rounded-[20px]" style={{ borderWidth: 1, borderColor: DESIGN_TOKENS.colors.borderStrong }}>
          {EVENT_FORMAT_OPTIONS.map((option, idx) => {
            const active = draft.eventFormat === option;
            return (
              <Pressable
                key={option}
                onPress={() => onChange({ eventFormat: active ? null : option })}
                className="flex-row items-center p-4"
                style={{
                  borderTopWidth: idx > 0 ? 1 : 0,
                  borderTopColor: DESIGN_TOKENS.colors.borderStrong,
                  backgroundColor: active ? `${DESIGN_TOKENS.colors.accentStrong}18` : DESIGN_TOKENS.colors.surfaceAlt,
                }}>
                <Text
                  variant="default"
                  className="flex-1"
                  style={{ color: active ? DESIGN_TOKENS.colors.accentStrong : DESIGN_TOKENS.colors.text, fontWeight: active ? '600' : '400' }}>
                  {getEventFormatLabel(option)}
                </Text>
                {active ? (
                  <View className="size-5 items-center justify-center rounded-full" style={{ backgroundColor: DESIGN_TOKENS.colors.accentStrong }}>
                    <Icon as={CheckIcon} size={11} style={{ color: '#000' }} />
                  </View>
                ) : null}
              </Pressable>
            );
          })}
        </View>
      </View>

      {isLoadingAttributes ? (
        <View className="rounded-[16px] border p-3" style={{ borderColor: DESIGN_TOKENS.colors.borderStrong, backgroundColor: DESIGN_TOKENS.colors.surfaceAlt }}>
          <Text variant="small" className="text-muted-foreground">Loading venue features</Text>
        </View>
      ) : null}

      {attributeLoadError ? (
        <View className="rounded-[16px] border p-3" style={{ borderColor: DESIGN_TOKENS.colors.borderStrong, backgroundColor: DESIGN_TOKENS.colors.surfaceAlt }}>
          <Text variant="small" className="font-semibold text-foreground">Venue features are unavailable</Text>
          <Text variant="small" className="mt-1 text-muted-foreground">{attributeLoadError}</Text>
        </View>
      ) : null}

      {!isLoadingAttributes && !attributeLoadError && attributes.length > 0 ? (
        <View className="gap-2">
          <Text variant="small" className="px-1 font-semibold text-muted-foreground">Venue features</Text>
          <View className="flex-row flex-wrap gap-2">
            {attributes.map((attribute) => {
              const active = draft.attributes.includes(attribute.slug);
              return (
                <TagChip
                  key={attribute.slug}
                  tagValue={attribute.slug}
                  label={attribute.label}
                  active={active}
                  disabled={false}
                  onPress={() =>
                    onChange({
                      attributes: active
                        ? draft.attributes.filter((v) => v !== attribute.slug)
                        : [...draft.attributes, attribute.slug],
                    })
                  }
                />
              );
            })}
          </View>
        </View>
      ) : null}

      <View className="overflow-hidden rounded-[20px]" style={{ borderWidth: 1, borderColor: DESIGN_TOKENS.colors.borderStrong, backgroundColor: DESIGN_TOKENS.colors.surfaceAlt }}>
        <Pressable
          className="flex-row items-center justify-between p-4"
          onPress={() => {
            onClosePicker();
            setShowAdvanced(!showAdvanced);
          }}>
          <View>
            <Text variant="default" className="font-semibold text-foreground">More options</Text>
            <Text variant="small" className="mt-0.5 text-muted-foreground">
              {advancedFilledCount > 0
                ? `${advancedFilledCount} optional field${advancedFilledCount > 1 ? 's' : ''} filled`
                : 'Everything below is optional'}
            </Text>
          </View>
          <Icon as={ChevronDownIcon} size={18} style={{ color: DESIGN_TOKENS.colors.textSoft, transform: [{ rotate: showAdvanced ? '180deg' : '0deg' }] }} />
        </Pressable>
      </View>

      {showAdvanced ? (
        <View className="gap-5">
          <View className="gap-2">
            <FieldMetaLabel label="Attire" meta="Optional" />
            <View className="flex-row flex-wrap gap-2">
              {ATTIRE_PRESET_OPTIONS.map((preset) => {
                const active = isDressCodePresetSelected(draft.dressCode, preset);
                return (
                  <Pressable
                    key={preset}
                    className="rounded-full border px-4 py-3"
                    style={{
                      borderColor: active ? DESIGN_TOKENS.colors.accentStrong : DESIGN_TOKENS.colors.borderStrong,
                      backgroundColor: active ? DESIGN_TOKENS.colors.accentStrong : DESIGN_TOKENS.colors.surfaceAlt,
                    }}
                    onPress={() => onChange({ dressCode: getNextDressCodeFromPreset(draft.dressCode, preset) })}>
                    <Text variant="small" className="font-semibold" style={{ color: active ? DESIGN_TOKENS.colors.textInverse : DESIGN_TOKENS.colors.text }}>
                      {preset}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
            <Input value={draft.dressCode} onFocus={onClosePicker} onChangeText={(value) => onChange({ dressCode: value })} placeholder="e.g. Smart casual, no sneakers" />
          </View>

          <View className="gap-2">
            <FieldMetaLabel label="Last entry" meta="Optional" />
            {Platform.OS === 'web' ? (
              <input
                type="time"
                value={draft.lastEntryTime}
                onChange={(event) => onChange({ lastEntryTime: event.target.value })}
                style={{
                  width: '100%',
                  height: 48,
                  borderRadius: 16,
                  border: `1px solid ${DESIGN_TOKENS.colors.borderStrong}`,
                  backgroundColor: DESIGN_TOKENS.colors.surfaceAlt,
                  color: DESIGN_TOKENS.colors.text,
                  fontSize: 15,
                  paddingLeft: 16,
                  paddingRight: 16,
                  colorScheme: 'light',
                } as React.CSSProperties}
              />
            ) : (
              <PickerTrigger
                label={draft.lastEntryTime.trim() ? formatTime(draft.lastEntryTime) : ''}
                placeholder="Select time"
                onPress={() => onOpenPicker('lastEntryTime')}
              />
            )}
            {Platform.OS === 'ios' && activePicker === 'lastEntryTime' ? (
              <View className="overflow-hidden rounded-[20px] border px-3 py-2" style={{ borderColor: DESIGN_TOKENS.colors.borderStrong, backgroundColor: DESIGN_TOKENS.colors.surfaceAlt }}>
                <DateTimePicker
                  value={getPickerValue('lastEntryTime', draft)}
                  mode="time"
                  display="spinner"
                  onChange={(_event: DateTimePickerEvent, selectedDate?: Date) => {
                    if (!selectedDate) return;
                    onPickerChange('lastEntryTime', selectedDate);
                  }}
                />
              </View>
            ) : null}
          </View>

          <View className="gap-2"><FieldMetaLabel label="Parking" meta="Optional" /><Input value={draft.parkingInfo} onFocus={onClosePicker} onChangeText={(value) => onChange({ parkingInfo: value })} placeholder="e.g. Free street parking on Commerce St" /></View>
          <View className="gap-2"><FieldMetaLabel label="Presented by" meta="Optional" /><Input value={draft.presentedBy} onFocus={onClosePicker} onChangeText={(value) => onChange({ presentedBy: value })} placeholder="e.g. Neighborhood Sounds" /></View>

          <View className="overflow-hidden rounded-[20px]" style={{ borderWidth: 1, borderColor: DESIGN_TOKENS.colors.borderStrong, backgroundColor: DESIGN_TOKENS.colors.surfaceAlt }}>
            <View className="flex-row items-center justify-between p-4">
              <View className="flex-1 pr-4">
                <Text variant="default" className="font-semibold text-foreground">Weather dependent</Text>
                <Text variant="small" className="mt-0.5 text-muted-foreground">Guests are warned the plan may shift.</Text>
              </View>
              <Switch value={draft.weatherDependent} onValueChange={(value) => onChange({ weatherDependent: value })} trackColor={{ false: '#3a3a3a', true: DESIGN_TOKENS.colors.accentStrong }} thumbColor="#ffffff" />
            </View>
          </View>

          {draft.weatherDependent ? (
            <Input value={draft.rainPlan} onFocus={onClosePicker} onChangeText={(value) => onChange({ rainPlan: value })} placeholder="e.g. Moves indoors if rain - check our Instagram" />
          ) : null}

          <View className="gap-2">
            <FieldMetaLabel label="Language" meta="Optional" />
            <View className="flex-row gap-2">
              {LANGUAGE_OPTIONS.map((option) => {
                const active = draft.language === option.value;
                return (
                  <Pressable
                    key={option.value}
                    className="flex-1 rounded-full border px-4 py-3"
                    style={{
                      borderColor: active ? DESIGN_TOKENS.colors.accentStrong : DESIGN_TOKENS.colors.borderStrong,
                      backgroundColor: active ? DESIGN_TOKENS.colors.accentStrong : DESIGN_TOKENS.colors.surfaceAlt,
                    }}
                    onPress={() => onChange({ language: option.value })}>
                    <Text variant="small" className="text-center font-semibold" style={{ color: active ? DESIGN_TOKENS.colors.textInverse : DESIGN_TOKENS.colors.text }}>
                      {option.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          <View className="gap-2"><FieldMetaLabel label="Contact email" meta="Optional" /><Input value={draft.contactEmail} onFocus={onClosePicker} onChangeText={(value) => onChange({ contactEmail: value })} placeholder="events@example.com" keyboardType="email-address" autoCapitalize="none" autoCorrect={false} /></View>
          <View className="gap-2"><FieldMetaLabel label="Contact phone" meta="Optional" /><Input value={draft.contactPhone} onFocus={onClosePicker} onChangeText={(value) => onChange({ contactPhone: value })} placeholder="(210) 555-0123" keyboardType="phone-pad" /></View>
          {draft.entryType !== EntryTypeTicketed ? (
            <View className="gap-2"><FieldMetaLabel label="Event website" meta="Optional" /><Input value={draft.eventUrl} onFocus={onClosePicker} onChangeText={(value) => onChange({ eventUrl: value })} placeholder="https://example.com/event" keyboardType="url" autoCapitalize="none" autoCorrect={false} /></View>
          ) : null}
          <View className="gap-2"><FieldMetaLabel label="Internal notes" meta="Optional" /><Input value={draft.internalNotes} onFocus={onClosePicker} onChangeText={(value) => onChange({ internalNotes: value })} placeholder="Only visible to your team" /></View>
        </View>
      ) : null}

      <View className="overflow-hidden rounded-[20px]" style={{ borderWidth: 1, borderColor: DESIGN_TOKENS.colors.borderStrong, backgroundColor: DESIGN_TOKENS.colors.surfaceAlt }}>
        <View className="px-4 pb-3 pt-4">
          <Text variant="default" className="font-semibold text-foreground">
            {mode === 'edit' ? 'Looks good?' : 'One last look'}
          </Text>
          <Text variant="small" className="mt-0.5 text-muted-foreground">
            {mode === 'edit' ? 'Review before saving.' : 'Make sure everything looks right.'}
          </Text>
        </View>

        <View style={hairline} />

        {[
          { label: 'Posting as', value: getPostingSummary(draft, linkedVenueID) },
          { label: 'Title', value: draft.title.trim() || 'Missing' },
          { label: 'Venue', value: draft.venueName.trim() || 'Missing' },
          { label: 'Time', value: startsAt ? `${formatDate(draft.startsDate)} · ${formatTime(draft.startsTime)}` : 'Missing' },
          { label: 'Entry', value: getEntrySummary(draft) },
          { label: 'Poster', value: getPosterSummary(draft) },
          { label: 'Age', value: formatEventAgeRestriction(draft.ageRestriction) },
          { label: 'Tags', value: draft.tags.length ? draft.tags.map(getEventTagLabel).join(', ') : 'Missing' },
        ].map((row, idx) => {
          const needsAttention = row.value === 'Missing' || row.value === 'Still uploading' || row.value === 'Upload needs attention';
          return (
            <React.Fragment key={row.label}>
              {idx > 0 ? <View style={hairline} /> : null}
              <View className="flex-row items-start justify-between gap-4 px-4 py-3">
                <Text variant="small" style={{ color: DESIGN_TOKENS.colors.textSoft }}>{row.label}</Text>
                <Text variant="small" className="flex-1 text-right font-medium" style={{ color: needsAttention ? '#ef4e2e' : DESIGN_TOKENS.colors.text }}>
                  {row.value}
                </Text>
              </View>
            </React.Fragment>
          );
        })}

        {!draft.posters.some((poster) => poster.status === 'uploaded') ? (
          <>
            <View style={hairline} />
            <View className="px-4 py-3">
              <Text variant="small" style={{ color: '#f59b80' }}>
                No flyer yet. Go back to step 1 to add one.
              </Text>
            </View>
          </>
        ) : null}
        <View style={hairline} />
        <View className="px-4 py-3">
          <Text variant="small" className="text-muted-foreground">
            Public events can go through a quick Radious review before they show everywhere.
          </Text>
        </View>
      </View>
    </View>
  );
}
